import axios from 'axios'
import {
  createVersionAssetUrl,
  createVersionedReloadUrl,
  evaluateBuildObservation,
  isChunkLoadError,
  isBodyContractErrorResponse,
  normalizeBuildId,
  shouldReloadAutomatically
} from './uiUpdatePolicy'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
const FIRST_CHECK_DELAY_MS = 30 * 1000
const FOCUS_CHECK_THROTTLE_MS = 30 * 1000
const BUILD_STABILITY_MS = 60 * 1000
const IDLE_RELOAD_MS = 8 * 1000
const ACTIVE_REQUEST_RETRY_MS = 500
const STORAGE_KEY = 'preika:latest-ui-build'
const NOTICE_ID = 'preika-ui-update-notice'
const CHUNK_RELOAD_KEY_PREFIX = 'preika:chunk-reload:'

let started = false
let activeRequests = 0
let checkingPromise = null
let dirty = false
let candidateBuildId = ''
let candidateFirstSeenAt = 0
let candidateTimer = null
let latestBuildId = ''
let lastCheckAt = 0
let lastInteractionAt = Date.now()
let lastNavigationTarget = ''
let pendingNavigationTarget = ''
let reloadTimer = null
let reloading = false
let bodyContractErrorSeen = false

function currentBuildId () {
  return normalizeBuildId(process.env.VUE_APP_BUILD_ID)
}

function isProductionBuild () {
  return process.env.NODE_ENV === 'production' && Boolean(currentBuildId())
}

function resolveStorage (name) {
  try {
    return window[name]
  } catch (error) {
    return null
  }
}

function safeStorageGet (storage, key) {
  if (!storage) return null
  try {
    return storage.getItem(key)
  } catch (error) {
    return null
  }
}

function safeStorageSet (storage, key, value) {
  if (!storage) return
  try {
    storage.setItem(key, value)
  } catch (error) {
    // Storage can be unavailable in privacy mode. UI update monitoring still works.
  }
}

function ensureNotice () {
  let notice = document.getElementById(NOTICE_ID)
  if (notice) return notice
  if (!document.body) return null

  notice = document.createElement('aside')
  notice.id = NOTICE_ID
  notice.className = 'preika-ui-update-notice'
  notice.setAttribute('role', 'status')
  notice.setAttribute('aria-live', 'polite')

  const title = document.createElement('strong')
  title.className = 'preika-ui-update-notice__title'

  const message = document.createElement('span')
  message.className = 'preika-ui-update-notice__message'

  notice.appendChild(title)
  notice.appendChild(message)
  document.body.appendChild(notice)
  return notice
}

/**
 * 検出しただけのときと、実際に処理が失敗したときで文面を変える。
 *
 * 後者は放置すると何度でも失敗するので、待つのではなく
 * 今すぐ再読み込みしてもらう必要がある。
 */
function noticeText () {
  // この文面が出るのは、版が違うと確認できたときだけ。
  // 版が同じなら bodyContractErrorSeen は announceUpdate で消える。
  if (bodyContractErrorSeen) {
    return {
      title: '更新版があるため処理できませんでした',
      message: '入力内容を控えてから、ブラウザを再読み込みしてください。'
    }
  }
  return {
    title: '更新版を検出しました',
    message: '入力中の内容を保護し、画面移動時に自動で切り替えます。'
  }
}

function showNotice () {
  const notice = ensureNotice()
  if (!notice) return

  const text = noticeText()
  const title = notice.querySelector('.preika-ui-update-notice__title')
  const message = notice.querySelector('.preika-ui-update-notice__message')
  if (title) title.textContent = text.title
  if (message) message.textContent = text.message

  notice.classList.add('preika-ui-update-notice--visible')
}

function hideNotice () {
  const notice = document.getElementById(NOTICE_ID)
  if (notice) notice.classList.remove('preika-ui-update-notice--visible')
}

function isDirtySensitiveTarget (target) {
  if (!target || target.nodeType !== 1) return false
  if (target.disabled || target.readOnly) return false

  if (target.closest) {
    if (target.closest('[data-preika-update-safe="true"]')) return false
    if (target.closest('.search-table')) return false
    if (target.closest('.preika-command-palette')) return false
  }

  const tagName = String(target.tagName || '').toLowerCase()
  if (tagName === 'textarea' || tagName === 'select') return true
  if (target.isContentEditable) return true
  if (tagName !== 'input') return false

  const type = String(target.type || 'text').toLowerCase()
  return !['button', 'submit', 'reset', 'hidden'].includes(type)
}

function markDirtyFromEvent (event) {
  if (!isDirtySensitiveTarget(event.target)) return
  dirty = true
  if (latestBuildId) showNotice()
}

function markInteraction () {
  lastInteractionAt = Date.now()
  if (latestBuildId && !dirty) scheduleReloadAttempt()
}

function finishTrackedRequest (config) {
  if (!config || !config.__preikaUiTrackedRequest) return
  config.__preikaUiTrackedRequest = false
  activeRequests = Math.max(0, activeRequests - 1)
  if (latestBuildId) scheduleReloadAttempt()
}

function installAxiosTracking () {
  axios.interceptors.request.use(config => {
    if (!config.__preikaUiVersionCheck) {
      activeRequests += 1
      config.__preikaUiTrackedRequest = true
    }
    return config
  }, error => Promise.reject(error))

  axios.interceptors.response.use(response => {
    finishTrackedRequest(response.config)
    return response
  }, error => {
    finishTrackedRequest(error && error.config)
    if (isBodyContractErrorResponse(error)) noteBodyContractError()
    return Promise.reject(error)
  })
}

/**
 * サーバから「画面が古い」と言われた。
 *
 * 定期確認は5分間隔なので、それを待たずに今すぐ確認しに行く。
 *
 * この合図は「本文を扱えなかった」でしかない。画面が古いとは限らず、
 * サーバ側の不具合でも起きる（実際に起きた）。だからここでは
 * 「確認を急ぐ」だけにして、古いと決めつけない。
 * 版が違うと分かったときだけ様子見の待ち時間を飛ばす。
 *
 * 入力途中なら自動再読込はしない。既存の dirty 判定に任せる。
 */
function noteBodyContractError () {
  bodyContractErrorSeen = true
  checkForUpdate(true)
}

function reloadTargetRoute () {
  return pendingNavigationTarget || lastNavigationTarget || window.location.hash || '#/'
}

function reloadWithBuild (buildId, routeFullPath) {
  if (reloading) return
  reloading = true
  hideNotice()
  const target = createVersionedReloadUrl(
    window.location.href,
    buildId,
    routeFullPath || reloadTargetRoute()
  )
  window.location.replace(target)
}

function attemptReload () {
  reloadTimer = null
  if (!latestBuildId || reloading) return

  const idleForMs = Date.now() - lastInteractionAt
  const canReload = shouldReloadAutomatically({
    dirty,
    activeRequests,
    visibilityState: document.visibilityState,
    idleForMs,
    idleThresholdMs: IDLE_RELOAD_MS
  })

  if (canReload) {
    reloadWithBuild(latestBuildId)
    return
  }

  if (dirty || activeRequests > 0) {
    showNotice()
  } else {
    hideNotice()
  }

  if (dirty) return

  const delay = activeRequests > 0
    ? ACTIVE_REQUEST_RETRY_MS
    : Math.max(100, IDLE_RELOAD_MS - idleForMs)
  reloadTimer = window.setTimeout(attemptReload, delay)
}

function scheduleReloadAttempt (delay = 0) {
  if (!latestBuildId || reloading) return
  if (reloadTimer !== null) window.clearTimeout(reloadTimer)
  reloadTimer = window.setTimeout(attemptReload, delay)
}

function clearCandidateTimer () {
  if (candidateTimer !== null) {
    window.clearTimeout(candidateTimer)
    candidateTimer = null
  }
}

function scheduleCandidateConfirmation () {
  clearCandidateTimer()
  const elapsed = Date.now() - candidateFirstSeenAt
  const delay = Math.max(250, BUILD_STABILITY_MS - elapsed)
  candidateTimer = window.setTimeout(() => checkForUpdate(true), delay)
}

function confirmUpdate (buildId) {
  candidateBuildId = ''
  candidateFirstSeenAt = 0
  clearCandidateTimer()
  latestBuildId = buildId
  safeStorageSet(resolveStorage('localStorage'), STORAGE_KEY, buildId)
  scheduleReloadAttempt()
  return true
}

function announceUpdate (metadata, alreadyConfirmed = false) {
  const buildId = normalizeBuildId(metadata && metadata.buildId)
  const now = Date.now()
  const observation = evaluateBuildObservation({
    currentBuildId: currentBuildId(),
    candidateBuildId,
    candidateFirstSeenAt,
    observedBuildId: buildId,
    now,
    stabilityMs: BUILD_STABILITY_MS,
    // サーバが「解釈できない」と言った時点で古いことは確定している。
    // 様子見の待ち時間は不要。
    alreadyConfirmed: alreadyConfirmed || bodyContractErrorSeen
  })

  if (observation === 'current') {
    candidateBuildId = ''
    candidateFirstSeenAt = 0
    clearCandidateTimer()
    // 版は同じだった。つまりサーバの苦情は画面の古さが原因ではない。
    // ここで合図を消さないと、このタブに残り続け、何時間も後の本当の
    // 配置で様子見の待ち時間を飛ばしてしまう。因果関係の無い
    // 「画面が古いため処理できませんでした」を出すことにもなる。
    bodyContractErrorSeen = false
    return false
  }

  if (observation === 'candidate') {
    candidateBuildId = buildId
    candidateFirstSeenAt = now
    scheduleCandidateConfirmation()
    return false
  }

  if (observation === 'wait') {
    scheduleCandidateConfirmation()
    return false
  }

  return confirmUpdate(buildId)
}

function checkForUpdate (force = false) {
  if (!isProductionBuild()) return Promise.resolve(false)
  if (checkingPromise) return checkingPromise

  const now = Date.now()
  if (!force && now - lastCheckAt < FOCUS_CHECK_THROTTLE_MS) {
    return Promise.resolve(false)
  }
  lastCheckAt = now

  checkingPromise = axios
    .get(createVersionAssetUrl(window.location.href, now), {
      timeout: 5000,
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      },
      __preikaUiVersionCheck: true
    })
    .then(response => announceUpdate(response.data || {}))
    .catch(() => false)
    .then(result => {
      checkingPromise = null
      return result
    }, error => {
      checkingPromise = null
      throw error
    })

  return checkingPromise
}

function handleVisibilityChange () {
  if (document.visibilityState === 'visible') {
    checkForUpdate()
  }
  if (latestBuildId) scheduleReloadAttempt()
}

function handleStorageEvent (event) {
  if (event.key !== STORAGE_KEY) return
  const buildId = normalizeBuildId(event.newValue)
  if (!buildId || buildId === currentBuildId()) return
  // Another tab writes this key only after the stability confirmation.
  announceUpdate({ buildId }, true)
}

function installRouterHooks (router) {
  router.beforeEach((to, from, next) => {
    lastNavigationTarget = to.fullPath
    if (!latestBuildId || reloading) {
      next()
      return
    }

    // The user already chose to leave the current screen. Load the requested
    // route from the new build instead of mounting another old component.
    dirty = false
    pendingNavigationTarget = to.fullPath
    lastInteractionAt = 0
    next(false)

    if (activeRequests === 0) {
      reloadWithBuild(latestBuildId, to.fullPath)
    } else {
      scheduleReloadAttempt()
    }
  })

  router.afterEach(to => {
    lastNavigationTarget = to.fullPath
    dirty = false
    pendingNavigationTarget = ''
    hideNotice()
  })

  router.onError(error => {
    if (!isChunkLoadError(error)) return

    // A lazy chunk usually disappears when a new dist replaces the old one.
    // The navigation itself means that leaving the current screen was intended.
    dirty = false
    pendingNavigationTarget = lastNavigationTarget
    lastInteractionAt = 0

    checkForUpdate(true).then(updateFound => {
      if (updateFound || latestBuildId) {
        scheduleReloadAttempt()
        return
      }

      if (candidateBuildId) {
        scheduleCandidateConfirmation()
        return
      }

      const buildId = currentBuildId()
      const reloadKey = CHUNK_RELOAD_KEY_PREFIX + buildId
      const sessionStorage = resolveStorage('sessionStorage')
      if (safeStorageGet(sessionStorage, reloadKey) === '1') return

      safeStorageSet(sessionStorage, reloadKey, '1')
      reloadWithBuild(buildId, lastNavigationTarget)
    })
  })
}

export function startUiUpdateMonitor (router) {
  if (started || !isProductionBuild()) return
  started = true

  installAxiosTracking()
  installRouterHooks(router)

  document.addEventListener('input', markDirtyFromEvent, true)
  document.addEventListener('change', markDirtyFromEvent, true)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', () => checkForUpdate())
  window.addEventListener('storage', handleStorageEvent)

  ;['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(eventName => {
    window.addEventListener(eventName, markInteraction, {
      capture: true,
      passive: true
    })
  })

  window.setTimeout(() => checkForUpdate(true), FIRST_CHECK_DELAY_MS)
  window.setInterval(() => checkForUpdate(), CHECK_INTERVAL_MS)
}

export const uiUpdateMonitorConstants = {
  CHECK_INTERVAL_MS,
  FIRST_CHECK_DELAY_MS,
  BUILD_STABILITY_MS,
  IDLE_RELOAD_MS
}
